import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import CloseIcon from "@mui/icons-material/Close";
import MessageIcon from "@mui/icons-material/Message";
import { Box, IconButton, Stack, type SxProps, type Theme, Typography, useTheme } from "@mui/material";
import { alpha, createTheme, ThemeProvider } from "@mui/material/styles";
import { motion, useReducedMotion } from "framer-motion";
import type { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BADGE, CONTROL_HEIGHT, FONT_SIZE, INK, LETTER_SPACING, MOTION } from "../../../../theme/tokens";
import type { PunchSummary, PunchSummaryCategory } from "../../../../utils/models";
import { PUNCH_SUMMARY_CATEGORY_DOT_HEX } from "../../../../utils/punchSummary";
import AddDayOffDialog from "../../../AddDayOffDialog/AddDayOffDialog";
import { InHouseTooltip } from "../../../InHouseTooltip/InHouseTooltip";
import { NewBroadcastMessageModal } from "../../../StaffCommunication/util/Broadcast/NewBroadcastMessageModal";
import { DeletePTODialog } from "../../../StaffTable/DeletePTODialog";
import { PTOMenu } from "../../../StaffTable/PTOMenu";
import { PtoRowActions } from "../../../StaffTable/PtoRowActions";
import SystemButton from "../../../UIToolkit/SystemButton/SystemButton";
import { getDefaultPunchMessage } from "./getDefaultPunchMessage";
import { ACTION_CENTER_MAX_HEIGHT } from "./punchActionCenterLayout";
import { ShiftDeltaChip, ShiftDeltaValues } from "./ShiftDeltaValues";
import { usePunchActionCenter } from "./usePunchActionCenter";

type Props = {
	activeDay: DateTime | null;
	activeCategory: PunchSummaryCategory | null;
	summary: PunchSummary | null;
	onClose: () => void;
	onSetSearch?: (name: string) => void;
};

const notifyAllBtnSx: SxProps<Theme> = { minWidth: 0, height: CONTROL_HEIGHT.COMPACT };
const rowNameSx = { color: INK.PRIMARY, fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, flex: 1, minWidth: 0 } as const satisfies SxProps<Theme>;
const rowSubLineSx = { color: INK.MUTED, fontSize: FONT_SIZE.CAPTION, fontWeight: 400, display: "block" } as const satisfies SxProps<Theme>;

export const PunchActionCenter: FC<Props> = ({ activeDay, activeCategory, summary, onClose, onSetSearch }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const reduceMotion = useReducedMotion();
	const {
		isOpen,
		rows,
		description,
		showNotifyButton,
		notifyAllRecipientCount,
		chosenDepartment,
		showNotifyAll,
		notifyAllNurseIds,
		handleNotifyAll,
		handleCloseNotifyAll,
		notifyForNurse,
		handleNotifyOne,
		handleCloseNotifyOne,
		handleEditPtoRow,
		handleDeletePtoRow,
		handleOpenPtoMenu,
		ptoMenuPreference,
		handleClosePtoMenu,
		handleEditPtoFromMenu,
		handleDeletePtoFromMenu,
		editingDayOffData,
		handleCloseEditingDayOff,
		handleSaveEditingDayOff,
		deleteDayOffData,
		deletePtoNurseId,
		isDeleting,
		handleCancelPtoDelete,
		handleConfirmDeletePto,
	} = usePunchActionCenter({ activeDay, activeCategory, summary });

	// Dialogs/menus/pickers rendered below portal to <body> and default their z-index off
	// theme.zIndex.modal. This popover pins itself to 1301 (see DayViewItem.tsx) so anything nested
	// here that stayed on the unmodified theme default (1300) would render underneath the popover
	// itself. Raising it locally covers every default-tier child — including ones added later —
	// without threading a zIndex prop through each one individually.
	const elevatedTheme = useMemo(() => createTheme(theme, { zIndex: { modal: 1303 } }), [theme]);

	if (!isOpen || !activeDay || !activeCategory || !description) return null;

	const categoryColor = PUNCH_SUMMARY_CATEGORY_DOT_HEX[activeCategory];

	return (
		<ThemeProvider theme={elevatedTheme}>
			{/* Three bands in a bounded column: the context header and the bulk action hold their place
			 * while only the list scrolls — and the panel as a whole never outgrows the space the popover
			 * can be nudged into (`ACTION_CENTER_MAX_HEIGHT`). */}
			<Box
				data-testid="punch-action-center"
				sx={{
					display: "flex",
					flexDirection: "column",
					maxHeight: ACTION_CENTER_MAX_HEIGHT,
					bgcolor: theme.palette.background.paper,
					// A thin category-colored flag along the top edge so the popover carries the same
					// categorical identity as the footer row that opened it — clipped to the rounded
					// corner by the parent popover shell (`SystemReactTinyPopover`'s contentRootStyle).
					borderTop: `3px solid ${categoryColor}`,
				}}
			>
				<Box sx={{ flexShrink: 0, borderBottom: `1px solid ${theme.palette.divider}`, px: 1.5, py: 1.25 }}>
					<Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
						<Box sx={{ flex: 1, minWidth: 0 }}>
							{/* The category IS what the panel is about, so it leads. "Action Center" named the
							 * component rather than anything on screen, and the count and total below it say
							 * more than that title did. */}
							<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
								<Box sx={{ width: BADGE.DOT_SIZE, height: BADGE.DOT_SIZE, borderRadius: "50%", bgcolor: categoryColor, flexShrink: 0 }} />
								<Typography
									variant="caption"
									sx={{ textTransform: "uppercase", color: theme.palette.text.secondary, fontWeight: 700, letterSpacing: LETTER_SPACING.LABEL }}
									noWrap
								>
									{t(description.labelKey)}
								</Typography>
							</Box>
							<Typography sx={{ fontSize: FONT_SIZE.TITLE_SM, color: INK.PRIMARY, lineHeight: 1.25, mt: 0.375 }}>
								{t("actionCenter.employeeCount", { count: description.employeeCount })}
							</Typography>
							{description.totalsText && (
								<Typography variant="caption" sx={{ display: "block", color: categoryColor, fontWeight: 600, mt: 0.25 }}>
									{description.totalsText}
								</Typography>
							)}
						</Box>
						<IconButton size="small" onClick={onClose} data-testid="punch-action-center-close" sx={{ mt: -0.5, mr: -0.5, flexShrink: 0 }}>
							<CloseIcon fontSize="small" />
						</IconButton>
					</Box>
				</Box>

				<Stack sx={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain", px: 1.5, py: 1, gap: 0.5 }}>
					{rows.map((row, index) => (
						<motion.div
							key={`${activeCategory}-${row.nurseId}`}
							initial={reduceMotion ? false : { opacity: 0, y: 6 }}
							animate={{ opacity: 1, y: 0 }}
							transition={
								reduceMotion
									? { duration: 0 }
									: {
											duration: MOTION.DURATION.ENTRANCE_SM / 1000,
											ease: MOTION.EASE_OUT_POINTS,
											delay: Math.min(index * MOTION.STAGGER.STEP, MOTION.STAGGER.MAX) / 1000,
										}
							}
						>
							<Box data-testid={`action-center-row-${row.nurseId}`}>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										py: 0.625,
										borderRadius: 1,
										px: 0.75,
										cursor: "pointer",
										transition: (theme) => theme.transitions.create("background-color", { duration: MOTION.DURATION.HOVER }),
										"@media (hover: hover) and (pointer: fine)": {
											"&:hover": { backgroundColor: alpha(categoryColor, 0.06) },
										},
									}}
									onDoubleClick={() => onSetSearch?.(row.name)}
								>
									<Box sx={{ flex: 1, minWidth: 0 }}>
										{/* No truncation on the name: at the panel's own width every name fits, and a
										 * wrapped name beats an ellipsized one when who the row is about is the point. */}
										<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
											<Typography sx={rowNameSx}>{row.name}</Typography>
											{activeCategory === "shiftDelta" && row.shiftDelta && <ShiftDeltaChip {...row.shiftDelta} />}
										</Box>
										{activeCategory === "shiftDelta" && row.shiftDelta ? (
											<ShiftDeltaValues {...row.shiftDelta} />
										) : (
											row.subLine && <Typography sx={rowSubLineSx}>{row.subLine}</Typography>
										)}
									</Box>
									{showNotifyButton && (activeCategory !== "agency" || row.missingPunch) && (
										<InHouseTooltip title={t("actionCenter.notifyOneFor", { name: row.name })}>
											<IconButton
												size="small"
												data-testid={`action-center-notify-${row.nurseId}`}
												onClick={(e) => {
													e.stopPropagation();
													handleNotifyOne(row);
												}}
											>
												<MessageIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
											</IconButton>
										</InHouseTooltip>
									)}
									{activeCategory === "pto" && row.preference && (
										<PtoRowActions
											onEdit={(event) => {
												event.stopPropagation();
												if (row.preference) handleEditPtoRow(row.preference);
											}}
											onDelete={(event) => {
												event.stopPropagation();
												if (row.preference) handleDeletePtoRow(row.preference);
											}}
											onMenuOpen={(event) => {
												event.stopPropagation();
												if (row.preference) handleOpenPtoMenu(event.currentTarget, row.preference);
											}}
										/>
									)}
								</Box>
							</Box>
						</motion.div>
					))}
				</Stack>

				{/* One click here messages everyone in the list, so it is deliberately not the loudest
				 * control in the panel — the per-row action is the considered one. It sits below the list
				 * instead of scrolling away with it. */}
				{showNotifyButton && (
					<Box sx={{ flexShrink: 0, borderTop: `1px solid ${theme.palette.divider}`, px: 1.5, py: 1.25 }}>
						<SystemButton
							buttonVariant="secondary"
							onClick={handleNotifyAll}
							leadingIcon={<CampaignOutlinedIcon fontSize="small" />}
							sx={notifyAllBtnSx}
							data-testid="action-center-notify-all"
						>
							{t("actionCenter.notifyAll")}
						</SystemButton>
						<Typography sx={{ display: "block", mt: 0.5, color: INK.MUTED, fontSize: FONT_SIZE.LABEL_XS }}>
							{t("actionCenter.notifyAllHint", { count: notifyAllRecipientCount })}
						</Typography>
					</Box>
				)}
			</Box>

			{showNotifyAll && notifyAllNurseIds && (
				<NewBroadcastMessageModal
					onClose={handleCloseNotifyAll}
					initialSelectedNurseIds={notifyAllNurseIds}
					lockRecipientSelection
					initialSmsMessage={getDefaultPunchMessage(t, activeCategory, activeDay, chosenDepartment.name ?? "")}
				/>
			)}

			{notifyForNurse && (
				<NewBroadcastMessageModal
					onClose={handleCloseNotifyOne}
					initialSelectedNurseIds={[notifyForNurse.nurseId]}
					lockRecipientSelection
					initialSmsMessage={getDefaultPunchMessage(t, activeCategory, activeDay, chosenDepartment.name ?? "")}
				/>
			)}

			{ptoMenuPreference && (
				<PTOMenu
					preference={ptoMenuPreference.pref}
					nurseId={ptoMenuPreference.nurseId}
					actionsMenuAnchorEl={ptoMenuPreference.anchor}
					onMenuClose={handleClosePtoMenu}
					onEditPTO={(p, nurseId) => handleEditPtoFromMenu(p.id, nurseId)}
					onDeletePTO={(p, nurseId) => handleDeletePtoFromMenu(p.id, nurseId)}
				/>
			)}

			{editingDayOffData && (
				<AddDayOffDialog
					open={!!editingDayOffData}
					onClose={handleCloseEditingDayOff}
					onSave={handleSaveEditingDayOff}
					nurseId={editingDayOffData.nurseId}
					dayOff={editingDayOffData.dayOff}
					zIndex={1302 /* sit above this popover's own 1301 */}
				/>
			)}

			<DeletePTODialog
				dayOff={deleteDayOffData}
				nurseId={deletePtoNurseId}
				isLoading={isDeleting}
				onConfirm={handleConfirmDeletePto}
				onCancel={handleCancelPtoDelete}
				zIndex={1302 /* sit above this popover's own 1301 */}
			/>
		</ThemeProvider>
	);
};
